import { useCallback, useEffect, useMemo, useState } from 'react';
import { ShopCategoriesGrid } from '~/components/ui/Shop';
import { ShopCategory } from '~/components/ui/ShopCategory';
import { rage, TattooItemConfig, useInputAction, useObservable } from '~/rage';
import { Gender, InputAction, TattooCategory } from '~/shared';
import {
	setActiveCategory,
	setActiveStage,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { categories, ICategory } from './CategoryTab.model';

// Кастомный хук для навигации с учетом недоступных категорий
const useTattooShopCategoryControls = (
	initialIndex: number | null,
	handleSelect: (index: number) => void,
	ownedTattoos: TattooItemConfig[] | null,
	filteredCategories: ICategory[],
) => {
	const [activeCategoryIndex, setActiveCategoryIndex] = useState<number | null>(
		initialIndex,
	);

	const getNextAvailableIndex = useCallback(
		(currentIndex: number, direction: number) => {
			let newIndex = currentIndex + direction;

			const hasTattoos = ownedTattoos && ownedTattoos.length > 0;

			while (newIndex >= 0 && newIndex < filteredCategories.length) {
				const category = filteredCategories[newIndex];

				if (category.type === 'delete' && !hasTattoos) {
					newIndex += direction;
					continue;
				}

				return newIndex;
			}

			if (direction > 0) {
				for (let i = filteredCategories.length - 1; i >= 0; i--) {
					const category = filteredCategories[i];
					if (category.type !== 'delete' || hasTattoos) {
						return i;
					}
				}
			} else {
				for (let i = 0; i < filteredCategories.length; i++) {
					const category = filteredCategories[i];
					if (category.type !== 'delete' || hasTattoos) {
						return i;
					}
				}
			}

			return currentIndex;
		},
		[filteredCategories, ownedTattoos],
	);

	const handleMenuNavigation = useCallback(
		(offset: number) => {
			if (activeCategoryIndex === null) {
				const firstAvailableIndex = getNextAvailableIndex(-1, 1);
				setActiveCategoryIndex(firstAvailableIndex);
				return;
			}

			const newIndex = getNextAvailableIndex(activeCategoryIndex, offset);
			setActiveCategoryIndex(newIndex);
		},
		[activeCategoryIndex, getNextAvailableIndex],
	);

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (activeCategoryIndex === null) {
			const firstAvailableIndex = getNextAvailableIndex(-1, 1);
			setActiveCategoryIndex(firstAvailableIndex);
			return;
		}

		if (handleSelect) {
			handleSelect(activeCategoryIndex);
		}
	});

	useInputAction(InputAction.CommonMenuNavUp, () => handleMenuNavigation(-3));
	useInputAction(InputAction.CommonMenuNavDown, () => handleMenuNavigation(3));
	useInputAction(InputAction.CommonMenuNavLeft, () => handleMenuNavigation(-1));
	useInputAction(InputAction.CommonMenuNavRight, () => handleMenuNavigation(1));

	return {
		activeCategoryIndex,
		setActiveCategoryIndex,
	};
};

const CategoryTab = () => {
	const dispatch = useLayoutDispatch();
	const activeCategory = useLayoutSelector(
		(state) => state.tattooShop.activeCategory,
	);
	const items = useObservable(rage.tattooShop.getItems());
	const ownedTattoos = useObservable(rage.tattooShop.getOwnedTattoos());
	const playerGender = useObservable(rage.player.getPlayerGender());

	const filteredCategories = useMemo(() => {
		return categories.filter((category) => {
			return (
				items?.some(
					(item) =>
						item.categoryId === category.type &&
						(item.gender === playerGender || item.gender === Gender.COMMON),
				) || category.type === 'delete'
			);
		});
	}, [items, playerGender]);

	const categoryUnseenMap = useMemo(() => {
		const map: Partial<Record<TattooCategory | 'delete', boolean>> = {};

		for (const category of filteredCategories) {
			if (category.type === 'delete') {
				continue;
			}

			const categoryItems = items?.filter(
				(item) =>
					item.categoryId === category.type &&
					(item.gender === playerGender || item.gender === Gender.COMMON),
			);

			if (!categoryItems || categoryItems.length === 0) {
				continue;
			}

			for (const item of categoryItems) {
				const seenTrackerKey = `${category.type}.${item.id}`;
				if (rage.tattooShop.isUnseen(seenTrackerKey)) {
					map[category.type] = true;
					break;
				}
			}
		}

		return map;
	}, [filteredCategories, items, playerGender]);

	const handleSelect = useCallback(
		(index: number) => {
			if (filteredCategories?.[index] === undefined) {
				return;
			}

			dispatch(setActiveStage(StageTypes.List));
			dispatch(setActiveCategory(filteredCategories[index].type));
		},
		[filteredCategories],
	);

	const { activeCategoryIndex, setActiveCategoryIndex } =
		useTattooShopCategoryControls(
			activeCategory
				? filteredCategories.map((item) => item.type).indexOf(activeCategory)
				: null,
			handleSelect,
			ownedTattoos,
			filteredCategories,
		);

	const handleSetActiveCategoryIndex = useCallback(
		(index: number) => {
			if (
				filteredCategories[index].type === 'delete' &&
				ownedTattoos?.length === 0
			) {
				return;
			}

			setActiveCategoryIndex(index);
		},
		[filteredCategories, ownedTattoos],
	);

	useEffect(() => {
		if (activeCategoryIndex === null) {
			dispatch(setActiveCategory(null));
			return;
		}

		dispatch(setActiveCategory(filteredCategories[activeCategoryIndex].type));
	}, [activeCategoryIndex]);

	useEffect(() => {
		if (activeCategoryIndex === 0 && ownedTattoos?.length === 0) {
			setActiveCategoryIndex(null);
		}
	}, [activeCategoryIndex, ownedTattoos]);

	return (
		<ShopCategoriesGrid style={{ marginTop: 30 }}>
			{filteredCategories.map((item, index) => {
				const seenTrackerKey = `${item.type}`;
				const isNew =
					item.type !== 'delete' &&
					(categoryUnseenMap[item.type] ??
						rage.tattooShop.isUnseen(seenTrackerKey));

				return (
					<ShopCategory
						name={item.name}
						icon={item.image}
						onClick={() => handleSelect(index)}
						isActive={activeCategory === item.type}
						isNew={isNew}
						key={index}
						onMouseEnter={() => handleSetActiveCategoryIndex(index)}
						onMouseLeave={() => setActiveCategoryIndex(null)}
						bgColor={item.type === 'delete' ? '#C71E1E' : undefined}
						disabled={item.type === 'delete' && ownedTattoos?.length === 0}
					/>
				);
			})}
		</ShopCategoriesGrid>
	);
};

export { CategoryTab };
